import * as L from "list";
import { registerDevtoolsFormatter } from "./DevtoolsFormatters";
import { ListFormatter } from "./ListDevtoolsFormatter";
import * as OrdModule from "./Ord";
import { toInternalOrd } from "./Ord";

export type Ord = OrdModule.Ord;

export class ListImpl<A> {
  [Symbol.iterator](): Iterator<A> {
    return this.l[Symbol.iterator]();
  }

  /**
   * The number of elements in the List
   */
  public length(): number {
    return L.length(this.l);
  }

  /**
   * Returns true if the List is empty
   */
  public isEmpty(): boolean {
    return L.length(this.l) === 0;
  }

  /**
   * Creates a new array with elements copied from this List.
   */
  public toArray(): A[] {
    return L.toArray(this.l);
  }

  /**
   * Sort a list, using the provided compare function.
   *
   * The resulting list will have the same length as the original list.
   */
  public sortBy<B extends Ord>(f: (a: A) => B): ListImpl<A> {
    return new ListImpl(L.sortBy((x) => toInternalOrd(f(x)), this.l));
  }

  /**
   * Returns a new List with the elements in reverse order.
   */
  public reverse(): ListImpl<A> {
    return new ListImpl(L.reverse(this.l));
  }

  /**
   * Iterate over the elements in the List in reverse order.
   */
  public backwards(): Iterable<A> {
    return L.backwards(this.l);
  }

  /**
   * Return the first element of the List, or `undefined` if the List is
   * empty
   */
  public first(): A | undefined {
    return L.first(this.l);
  }

  /**
   * Return the last element of the List, or `undefined` if the List is
   * empty
   */
  public last(): A | undefined {
    return L.last(this.l);
  }

  /**
   * Returns the element at the provided index, or `undefined` if the
   * index is out of bounds
   */
  public nth(index: number): A | undefined {
    return L.nth(index, this.l);
  }

  /**
   * Create a new List with the results of calling the provided function
   * on every element in the List.
   *
   * The resulting list will have the same length as the original list.
   *
   * Always returns a new List.
   */
  public map<B>(f: (a: A) => B): ListImpl<B> {
    return new ListImpl(L.map(f, this.l));
  }

  /**
   * Create a new List with the results of calling the provided function
   * on every element in the List, and then concatenating all of those
   * results together.
   *
   * Always returns a new List.
   */
  public flatMap<B>(f: (a: A) => ListImpl<B>): ListImpl<B> {
    return new ListImpl(L.flatMap((x) => f(x).l, this.l));
  }

  /**
   * Add one element to the end of the List
   */
  public append(value: A): ListImpl<A> {
    return new ListImpl(L.append(value, this.l));
  }

  /**
   * Add one element to the beginning of the List
   */
  public prepend(value: A): ListImpl<A> {
    return new ListImpl(L.prepend(value, this.l));
  }

  /**
   * Return a new List, containing the first `n` elements from this List.
   *
   * Always returns a new List.
   */
  public take(n: number): ListImpl<A> {
    return new ListImpl(L.take(n, this.l));
  }

  /**
   * Returns a new List with only the values for which the `predicate`
   * function returns true.
   *
   * If no elements were removed, then the original List is returned.
   */
  public filter(predicate: (a: A) => boolean): ListImpl<A> {
    const updated = L.filter(predicate, this.l);
    if (updated.length === this.l.length) {
      // List has not changed
      return this;
    } else {
      return new ListImpl(updated);
    }
  }

  public strictFilter<S extends A>(predicate: (a: A) => a is S): ListImpl<S> {
    const updated = L.filter(predicate, this.l);
    if (updated.length === this.l.length) {
      // List has not changed
      return this as unknown as ListImpl<S>;
    } else {
      return new ListImpl(updated);
    }
  }

  /**
   * Returns the value of the first element in the array that satisfies
   * the provided testing function. Otherwise `undefined` is returned.
   */
  public find(predicate: (a: A) => boolean): A | undefined {
    return L.find(predicate, this.l);
  }

  /**
   * Find the first element in the array that satisfies the provided
   * testing function, and then modifies that element in place using the
   * provided `modify` function.
   *
   * If no matching element was found, then the original List is
   * returned.
   *
   * If an element was found, but the `modify` function returned the old
   * value unchanged, then the original List is returned.
   *
   */
  public findAndModify(predicate: (a: A) => boolean, modify: (value: A) => A): ListImpl<A> {
    const index = L.findIndex(predicate, this.l);
    if (index < 0) {
      return this;
    } else {
      const newL = L.adjust(index, modify, this.l);
      if (L.nth(index, newL) === L.nth(index, this.l)) {
        return this;
      } else {
        return new ListImpl(newL);
      }
    }
  }

  /**
   * Tests whether at least one element in the List passes the test
   * implemented by the provided function.
   *
   * If the List is empty returns false.
   */
  public some(predicate: (a: A) => boolean): boolean {
    return L.some(predicate, this.l);
  }

  /**
   * Tests whether none of the elements in the List pass the test
   * implemented by the provided function.
   *
   * If the List is empty returns true.
   */
  public none(predicate: (a: A) => boolean): boolean {
    return L.none(predicate, this.l);
  }

  /**
   * Concatenates two Lists together.
   *
   * If the other list is empty, then the original list is returned.
   *
   * If the current list is empty, then the other list is returned.
   */
  public concat(other: ListImpl<A>): ListImpl<A> {
    if (other.l.length === 0) {
      return this;
    } else if (this.l.length === 0) {
      return other;
    } else {
      return new ListImpl(L.concat(this.l, other.l));
    }
  }

  /**
   * Creates a new list where each element is a pair of elements of both
   * input lists.
   *
   * The length of the resulting List will be the length of the shorter
   * input List
   */
  public zip<B>(other: ListImpl<B>): ListImpl<[A, B]> {
    return new ListImpl(L.zip(this.l, other.l));
  }

  /**
   * Creates a new list where each element is paired with its index in
   * the List.
   */
  public zipWithIndex(): ListImpl<[A, number]> {
    return new ListImpl(L.zip(this.l, L.range(0, this.l.length)));
  }

  public constructor(list: L.List<A>) {
    this.l = list;
  }

  protected l: L.List<A>;
}

export class KeyedList<A, F extends keyof A> extends ListImpl<A> {
  public readonly keyField: F;

  public static empty<A>(): <F extends keyof A>(keyField: F) => KeyedList<A, F> {
    return (keyField) => {
      return new KeyedList(L.empty(), keyField);
    };
  }

  public static from<A>(
    sequence: A[] | ArrayLike<A> | Iterable<A>
  ): <F extends keyof A>(keyField: F) => KeyedList<A, F> {
    return (keyField) => {
      return new KeyedList(L.from(sequence), keyField);
    };
  }

  [Symbol.iterator](): Iterator<A> {
    return this.l[Symbol.iterator]();
  }

  public sortBy<B extends Ord>(f: (a: A) => B): KeyedList<A, F> {
    return new KeyedList(
      L.sortBy((x) => toInternalOrd(f(x)), this.l),
      this.keyField
    );
  }

  public reverse(): KeyedList<A, F> {
    return new KeyedList(L.reverse(this.l), this.keyField);
  }

  public last(): A | undefined {
    return L.last(this.l);
  }

  public map<B>(f: (a: A) => B): ListImpl<B> {
    return new ListImpl(L.map(f, this.l));
  }

  public mapKeyedList<B, G extends keyof B>(keyField: G, f: (a: A) => B): KeyedList<B, G> {
    return new KeyedList(L.map(f, this.l), keyField);
  }

  public append(value: A): KeyedList<A, F> {
    return new KeyedList(L.append(value, this.l), this.keyField);
  }

  public prepend(value: A): KeyedList<A, F> {
    return new KeyedList(L.prepend(value, this.l), this.keyField);
  }

  public findAndModify(predicate: (a: A) => boolean, modify: (value: A) => A): KeyedList<A, F> {
    const index = L.findIndex(predicate, this.l);
    if (index < 0) {
      return this;
    } else {
      const newL = L.adjust(index, modify, this.l);
      if (L.nth(index, newL) === L.nth(index, this.l)) {
        return this;
      } else {
        return new KeyedList(newL, this.keyField);
      }
    }
  }

  private constructor(list: L.List<A>, keyField: F) {
    super(list);
    this.keyField = keyField;
  }
}

registerDevtoolsFormatter(ListFormatter);
